import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Loader2 } from 'lucide-react'
import { showSuccess, showError, showLoading, closeSwal } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { shiftService } from '@/services/shiftService'
import type { EmpShiftsVm } from '@/api/types'

const SHIFT_TYPE_VALUES = ['Morning', 'Evening', 'Night', 'General'] as const

export const ShiftDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0
  const isEditMode = id !== 'new' && id != null

  const shiftTypeLabels = useMemo(
    () => ({
      Morning: t('shiftPage.shiftTypeMorning'),
      Evening: t('shiftPage.shiftTypeEvening'),
      Night: t('shiftPage.shiftTypeNight'),
      General: t('shiftPage.shiftTypeGeneral'),
    }),
    [t]
  )

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [shift, setShift] = useState<EmpShiftsVm>({
    id: 0,
    branchId: branchId ?? 0,
    shiftType: '',
    startTime: '',
    endTime: '',
    description: '',
  })

  useEffect(() => {
    if (!branchId) return
    setShift((prev) => ({ ...prev, branchId }))
  }, [branchId])

  useEffect(() => {
    if (!isEditMode || !id || !branchId) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await shiftService.getById(Number(id), branchId)
        setShift({
          id: data.id,
          branchId: data.branchId ?? branchId,
          shiftType: data.name ?? data.shiftType ?? '',
          startTime: (data.shiftStartTime ?? data.startTime)
            ? toInputTime(String(data.shiftStartTime ?? data.startTime ?? ''))
            : '',
          endTime: (data.shiftEndTime ?? data.endTime)
            ? toInputTime(String(data.shiftEndTime ?? data.endTime ?? ''))
            : '',
          description: data.shiftDescription ?? data.description ?? '',
        })
      } catch (e: any) {
        console.error(e)
        setShift((prev) => ({ ...prev, id: Number(id) }))
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isEditMode, id, branchId])

  function toInputTime(val: string): string {
    if (!val) return ''
    if (/^\d{2}:\d{2}$/.test(val)) return val
    try {
      const d = new Date(val)
      const h = d.getHours()
      const m = d.getMinutes()
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    } catch {
      return val
    }
  }

  /** Convert "HH:mm" to full ISO datetime (today's date + time) for API */
  function timeToISO(timeStr: string | undefined): string {
    if (!timeStr || !/^\d{1,2}:\d{2}$/.test(timeStr)) return new Date().toISOString()
    const [h, m] = timeStr.split(':').map(Number)
    const d = new Date()
    d.setHours(h, m, 0, 0)
    return d.toISOString()
  }

  const handleChange = (field: keyof EmpShiftsVm, value: string | number | undefined) => {
    setShift((prev) => ({ ...prev, [field]: value }))
  }

  const handleSave = async () => {
    if (!branchId) {
      await showError(t('shiftPage.validationErrorTitle'), t('shiftPage.toastSelectBranch'), 4000)
      return
    }
    if (!shift.description?.trim()) {
      await showError(t('shiftPage.validationErrorTitle'), t('shiftPage.toastDescriptionRequired'), 4000)
      return
    }
    try {
      setSaving(true)
      showLoading(
        isEditMode ? t('shiftPage.loadingUpdating') : t('shiftPage.loadingSaving'),
        t('shiftPage.loadingPleaseWait')
      )
      const payload = {
        id: shift.id ?? 0,
        name: shift.shiftType ?? shift.description?.trim() ?? '',
        shiftStartTime: timeToISO(shift.startTime),
        shiftEndTime: timeToISO(shift.endTime),
        shiftDescription: shift.description?.trim() ?? '',
        branchId,
      }
      console.log('🔵 [ShiftDetail] Save payload (API structure):', JSON.stringify(payload, null, 2))
      if (isEditMode && shift.id) {
        await shiftService.update(payload)
      } else {
        await shiftService.create(payload)
      }
      closeSwal()
      await showSuccess(
        isEditMode ? t('shiftPage.savedUpdatedTitle') : t('shiftPage.savedCreatedTitle'),
        isEditMode ? t('shiftPage.savedUpdatedMessage') : t('shiftPage.savedCreatedMessage')
      )
      navigate('/shifts')
    } catch (error: any) {
      closeSwal()
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        t('shiftPage.saveFailedMessage')
      await showError(t('shiftPage.saveFailedTitle'), msg, 5000)
    } finally {
      setSaving(false)
    }
  }

  const req = t('shiftPage.requiredMark')

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={t('shiftPage.pageTitle')} description={t('shiftPage.pageDescription')} />
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title={t('shiftPage.branchRequiredTitle')}
              description={t('shiftPage.branchRequiredDescriptionForm')}
            />
            <Button className="mt-4" variant="outline" onClick={() => navigate('/shifts')}>
              {t('shiftPage.return')}
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader title={t('shiftPage.pageTitle')} description={t('shiftPage.pageDescription')} />

      <Card>
        <CardHeader>
          <CardTitle>{t('shiftPage.cardTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>
                    {req} {t('shiftPage.labelShiftType')}
                  </Label>
                  <Select value={shift.shiftType || ''} onValueChange={(v) => handleChange('shiftType', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('shiftPage.placeholderSelectShiftType')} />
                    </SelectTrigger>
                    <SelectContent>
                      {SHIFT_TYPE_VALUES.map((value) => (
                        <SelectItem key={value} value={value}>
                          {shiftTypeLabels[value]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('shiftPage.labelStartTime')}</Label>
                  <Input
                    type="time"
                    value={shift.startTime || ''}
                    onChange={(e) => handleChange('startTime', e.target.value)}
                    disabled={saving}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('shiftPage.labelEndTime')}</Label>
                  <Input
                    type="time"
                    value={shift.endTime || ''}
                    onChange={(e) => handleChange('endTime', e.target.value)}
                    disabled={saving}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>
                  {req} {t('shiftPage.labelDescription')}
                </Label>
                <Textarea
                  placeholder={t('shiftPage.placeholderDescription')}
                  value={shift.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  disabled={saving}
                  rows={4}
                  className="resize-y"
                />
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => navigate('/shifts')} disabled={saving}>
              {t('shiftPage.return')}
            </Button>
            <Button onClick={handleSave} disabled={saving || loading}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('shiftPage.saving')}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {t('shiftPage.save')}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
